import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { get as _get } from 'lodash';
import { getAutoServiceEligibility, getPropertyServiceEligibility } from '../../../api/getEligibleServicesApi';
import { PostMessageCodes } from '../../../Constants/PostMessageCodes';
import {
  AutoEligibilityServiceCallArguments,
  AutoEligibilityServicesInfo,
  EligibleServicesConstants,
  Features,
  PropertyEligibilityServiceCallArguments,
  PropertyEligibilityServicesInfo,
  PropertyEligibilityServicesResponse,
  PropertyServicesInfo,
  ProviderInfo,
  ServiceType,
  StageNames,
  StageState,
} from '../../../types/type';
import {
  getStagesInOrder,
  getStaticProgressStages,
  getWebUrl,
  isAutoEligibleService,
  postMessageToParentApp,
} from '../../../utils/Utilities';
import { isPartnerFeatureEnabled } from '../../featureToggle/featureToggle';

export interface State {
  eligibleEmergencyService: PropertyServicesInfo | undefined;
  eligibleWaterService: PropertyServicesInfo | undefined;
  eligibleRepairService: any;
  eligibleRentalService: any;
  eligibleCvpService: any;
  availableProgressStages: StageState[];
  selectedServiceProviders: ProviderInfo[];
  staticProgressStages: StageState[];
}

const initialState: State = {
  eligibleEmergencyService: undefined,
  eligibleWaterService: undefined,
  eligibleRepairService: undefined,
  eligibleRentalService: undefined,
  eligibleCvpService: undefined,
  availableProgressStages: [],
  selectedServiceProviders: [],
  staticProgressStages: getStaticProgressStages(),
};

export async function getPropertyEligibleServices(params: PropertyEligibilityServiceCallArguments) {
  const response: PropertyEligibilityServicesResponse = await getPropertyServiceEligibility(
    params.claimNumber,
    params.urlKey,
    params.sessionId,
    params.authKey,
    params.zipCode
  );
  return response;
}

export async function getAutoEligibleServices(params: AutoEligibilityServiceCallArguments) {
  if (_get(params, 'vin')) {
    const response = await getAutoServiceEligibility(
      params.claimNumber,
      params.urlKey,
      params.sessionId,
      params.authKey,
      params.vin
    );
    if (Number(_get(response, 'response.status.code', '')) !== 200) {
      postMessageToParentApp(
        PostMessageCodes.DIGITAL_CLAIM_V2_AUTO_ELIGIBLE_SERVICES_ERROR,
        params.claimNumber,
        response
      );
    }
    return response;
  } else {
    return null;
  }
}

const servicesSlice = createSlice({
  name: 'services',
  initialState,
  reducers: {
    updateStageState(state, action: PayloadAction<{ updatedStages: StageState[] }>) {
      const { updatedStages } = action.payload;
      state.availableProgressStages = updatedStages;
    },
    updateSelectedProvidersState(state, action: PayloadAction<{ selectedProvider: ProviderInfo }>) {
      const { selectedProvider } = action.payload;
      state.selectedServiceProviders = [...state.selectedServiceProviders, selectedProvider];
    },
    resetSelectedProvidersState(state, _action: unknown) {
      state.selectedServiceProviders = [];
    },
    resetAvailableProgressStagesState(state, _action: unknown) {
      state.availableProgressStages = []; // Reset available services
    },
    updateEligibleServiceState(
      state,
      action: PayloadAction<{
        response: PropertyEligibilityServicesInfo | AutoEligibilityServicesInfo;
      }>
    ) {
      const { response } = action.payload;
      updateStateWithEligibleServices(state, response);
    },
    resetEligibleServiceState(state, _action: unknown) {
      state.eligibleCvpService = undefined;
      state.eligibleRepairService = undefined;
      state.eligibleRentalService = undefined;
      state.eligibleEmergencyService = undefined;
      state.eligibleWaterService = undefined;
    },
    clearCvpEligibility(state, _action: unknown) {
      // Business rule: CVP is only available when there's no DRP shop selected, so a
      // successful DRP assignment always makes the claim CVP-ineligible from that point on.
      state.eligibleCvpService = undefined;
    },
  },
});

function updateStateWithEligibleServices(
  state: State,
  response: PropertyEligibilityServicesInfo | AutoEligibilityServicesInfo
) {
  if (response) {
    if (response.status.code === 200) {
      if (!isAutoEligibleService(response)) {
        //Filter the results based on serviceType 'WATER_MITIGATION_TYPE'
        const filteredWaterServices = response.eligibleServices.filter(
          (service) => service.serviceType === ServiceType.WATER_MITIGATION_TYPE
        );
        if (filteredWaterServices.length > 0) {
          // Filter Providers based on Provider's service availablity
          const filteredWaterServicesWithFilteredProviders: PropertyServicesInfo = {
            ...filteredWaterServices[0],
            providers: getProviderWithServiceAvailabilty(filteredWaterServices[0].providers),
          };
          // Add the website information if it is not present in the web service response before updating the state.
          const eligibleWaterService = servicesWithWebUrlInfo(filteredWaterServicesWithFilteredProviders);
          state.eligibleWaterService = eligibleWaterService;
        }

        //Filter the results based on serviceType 'EMERGENCY_SERVICE_TYPE'
        const filteredEmergencyServices = response.eligibleServices.filter(
          (service) => service.serviceType === ServiceType.EMERGENCY_SERVICE_TYPE
        );
        if (filteredEmergencyServices.length > 0) {
          // Filter Providers based on Provider's service availablity
          const filteredEmergencyServicesWithFilteredProviders: PropertyServicesInfo = {
            ...filteredEmergencyServices[0],
            providers: getProviderWithServiceAvailabilty(filteredEmergencyServices[0].providers),
          };
          // Add the website information if it is not present in the web service response before updating the state.
          state.eligibleEmergencyService = servicesWithWebUrlInfo(filteredEmergencyServicesWithFilteredProviders);
        }
      } else {
        //Filter the results based on EligibleServicesConstants 'DRP'
        const filteredRepairServices = response.eligibleServices.filter(
          (service) => service.serviceCode === EligibleServicesConstants.DRP
        );
        if (filteredRepairServices.length > 0) {
          // Add the website information if it is not present in the web service response before updating the state.
          state.eligibleRepairService = filteredRepairServices[0];
        }

        //Filter the results based on EligibleServicesConstants 'CVP'
        const filteredCvpServices = response.eligibleServices.filter(
          (service) => service.serviceCode === EligibleServicesConstants.CVP
        );
        if (filteredCvpServices.length > 0) {
          // Add the website information if it is not present in the web service response before updating the state.
          state.eligibleCvpService = filteredCvpServices[0];
        }

        //Filter the results based on EligibleServicesConstants 'Rental'
        const filteredRentalServices = response.eligibleServices.filter(
          (service) => service.serviceCode === EligibleServicesConstants.RENTAL
        );
        if (filteredRentalServices.length > 0) {
          // Add the website information if it is not present in the web service response before updating the state.
          state.eligibleRentalService = filteredRentalServices[0];
        }
      }
      // Create a collection of Stage states
      state.availableProgressStages = getStageStates(response, state);
    }
  }
}
export function servicesWithWebUrlInfo(service: PropertyServicesInfo): PropertyServicesInfo {
  const updatedProviders = service.providers.map((provider) => {
    if (!provider?.providerUrl && provider.providerID) {
      const url = getWebUrl(provider.providerID);
      provider = { ...provider, providerUrl: url };
    }
    return provider;
  });
  const updatedInfo = { ...service, providers: updatedProviders };
  return updatedInfo;
}

function getProviderWithServiceAvailabilty(providers: ProviderInfo[]): ProviderInfo[] {
  return providers.filter((provider) => provider.serviceAvailable.toLowerCase() !== 'Unavailable'.toLowerCase());
}

function getStageStates(response: PropertyEligibilityServicesInfo | AutoEligibilityServicesInfo, state: any) {
  // This is the central data object which tracks different states. Some properties are set by web service response, some are set by UI and some are added by default
  const stageStates: StageState[] = [];
  // Add stage 'Submit' by default
  const reportStage: StageState = {
    name: StageNames.SUBMIT, // Set by default
    completed: true, // Set by default
    viewActive: false, // Set by default
    skipped: false, // Set by default
    showProgress: false, // Set by UI action
    orderOfDisplay: 0, // Set by default
    id: Features.SUBMIT_STAGE, // Set by default
  };
  stageStates.push(reportStage);

  if (!isAutoEligibleService(response)) {
    // Check for Emergency Services
    const isEmergencyServiceOpen =
      state.eligibleEmergencyService && state.eligibleEmergencyService.serviceOpen ? true : false;
    const isEmergencyServiceEligible =
      state.eligibleEmergencyService && state.eligibleEmergencyService.providers.length ? true : false;
    const emergencyStage: StageState = {
      name: StageNames.EMERGENCY_SERVICES, // Set by default
      completed: false, // Set by UI action
      viewActive: false, // Set by UI action
      skipped: false, // Set by UI action
      showProgress: false, // Set by UI action
      orderOfDisplay: 1, // Set by default
      id: Features.EMERGENCY_SERVICES, // Set by default
      isEligible: isEmergencyServiceEligible, // Set by web service response value
      isServiceOpen: isEmergencyServiceOpen, // Set by web service response value
    };
    if (
      isPartnerFeatureEnabled(response.opCo, Features.EMERGENCY_SERVICES) &&
      emergencyStage.isEligible &&
      !emergencyStage.isServiceOpen
    ) {
      stageStates.push(emergencyStage);
    }
    // Check for Water Removal
    const isWaterRemovalServiceOpen =
      state.eligibleWaterService && state.eligibleWaterService.serviceOpen ? true : false;
    const isWaterRemovalServiceEligible =
      state.eligibleWaterService && state.eligibleWaterService.providers.length ? true : false;
    const waterRemovalStage: StageState = {
      name: StageNames.WATER_REMOVAL, // Set by default
      completed: false, // Set by UI action
      viewActive: false, // Set by UI action
      skipped: false, // Set by UI action
      showProgress: false, // Set by UI action
      orderOfDisplay: 2, // Set by default
      id: Features.WATER_REMOVAL, // Set by default
      isEligible: isWaterRemovalServiceEligible, // Set by web service response value
      isServiceOpen: isWaterRemovalServiceOpen, // Set by web service response value
    };
    if (
      isPartnerFeatureEnabled(response.opCo, Features.WATER_REMOVAL) &&
      waterRemovalStage.isEligible &&
      !waterRemovalStage.isServiceOpen
    ) {
      stageStates.push(waterRemovalStage);
    }
    // Check for Planer
    // Check eligibility in response
    const isPlanrEligible = _get(response, 'plnarEligible', false);
    const documentDamageStage: StageState = {
      name: StageNames.DOCUMENT_DAMAGE, // Set by default
      completed: false, // Set by UI action
      viewActive: false, // Set by UI action
      skipped: false, // Set by UI action
      showProgress: false, // Set by UI action
      orderOfDisplay: 3, // Set by default
      id: Features.DOCUMENT_DAMAGE, // Set by default
      isEligible: isPlanrEligible, // Set by web service response value
      isServiceOpen: false, // Set by web service response value
    };
    if (isPartnerFeatureEnabled(response.opCo, Features.DOCUMENT_DAMAGE) && documentDamageStage.isEligible) {
      stageStates.push(documentDamageStage);
    }

    // Add Complete Stage by default
    const completeStage: StageState = {
      name: StageNames.COMPLETE, // Set by default
      completed: false, // Set by UI action
      viewActive: false, // Set by UI action
      skipped: false, // Set by UI action
      showProgress: false, // Set by UI action
      orderOfDisplay: 4, // Set by default
      id: Features.COMPLETE_STAGE, // Set by default
      isEligible: true, // Set by default
    };
    stageStates.push(completeStage);
  } else {
    // Setup Service Stages for Auto
    const isRepairEligible = _get(response, 'eligibleServices', []).some(
      (service) => service.serviceCode === EligibleServicesConstants.DRP
    );
    const isRepairSelected = _get(response, 'eligibleServices', []).some(
      (service) => service.serviceCode === EligibleServicesConstants.DRP && service.serviceOpen
    );
    if (isRepairEligible) {
      const repairStage: StageState = {
        name: StageNames.REPAIR, // Set by default
        completed: false, // Set by UI action
        viewActive: false, // Set by UI action
        skipped: false, // Set by UI action
        showProgress: false, // Set by UI action
        orderOfDisplay: 1, // Set by default
        id: Features.REPAIR, // Set by default
        isEligible: isRepairEligible, // Set by web service response value
        isServiceOpen: isRepairSelected, // Set by web service response value
      };

      if (isRepairSelected) {
        repairStage.completed = true;
      }
      stageStates.push(repairStage);
    }

    const isPhotosSelected = response.numberOfCustomerPhotos > 0;
    // Setup Service Stages for Auto

    const photosStage: StageState = {
      name: StageNames.PHOTOS, // Set by default
      completed: isPhotosSelected, // Set by UI action
      viewActive: false, // Set by UI action
      skipped: false, // Set by UI action
      showProgress: false, // Set by UI action
      orderOfDisplay: 2, // Set by default
      id: Features.PHOTOS, // Set by default
      isEligible: true, // Set by default
      isServiceOpen: isPhotosSelected, // Set by web service response value
    };

    if (isPhotosSelected) {
      photosStage.completed = true;
    }
    stageStates.push(photosStage);

    // Setup Service Stages for Auto
    const isRentalEligible = _get(response, 'eligibleServices', []).some(
      (service) =>
        service.serviceCode === EligibleServicesConstants.RENTAL && service.serviceCustomerCallInMsgCode === ''
    );
    const isRentalSelected = _get(response, 'eligibleServices', []).some(
      (service) => service.serviceCode === EligibleServicesConstants.RENTAL && service.serviceOpen
    );
    const rentalStage: StageState = {
      name: StageNames.RENTAL, // Set by default
      completed: isRentalSelected, // Set by UI action
      viewActive: false, // Set by UI action
      skipped: false, // Set by UI action
      showProgress: false, // Set by UI action
      orderOfDisplay: 3, // Set by default
      id: Features.RENTAL, // Set by default
      isEligible: true, // Set by web service response value
      isServiceOpen: isRentalSelected, // Set by web service response value
    };

    if (isRentalSelected) {
      rentalStage.completed = true;
    }
    stageStates.push(rentalStage);
  }

  const orderedStages = getStagesInOrder(stageStates);
  return orderedStages;
}

export const {
  updateStageState,
  updateSelectedProvidersState,
  resetSelectedProvidersState,
  updateEligibleServiceState,
  resetAvailableProgressStagesState,
  resetEligibleServiceState,
  clearCvpEligibility,
} = servicesSlice.actions;
export default servicesSlice.reducer;
